import type { BreakdownStep } from '../../types/journey'
import { WEEKDAY_CHIPS, quickSpec } from '../../utils/habitForm'
import { buildRRule, deriveLegacyCadence, deriveLegacyPreferredDays } from '../../utils/rrule'
import { HabitStepFields, SkillStepFields, TaskStepFields, type Update } from './StepKindFields'

/**
 * Journey step kinds, shared by the skill and goal journeys: which fields a
 * step of each kind asks for in the review, and the two mappings between the
 * review's shape and the API's (a habit becomes an RRULE; the engine's
 * suggested habit_plan becomes the chooser's state).
 */
const pad = (n: number) => String(n).padStart(2, '0')
const todayLocalISO = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

/** The per-kind fields, by kind. */
export function stepFieldsFor(step: BreakdownStep, update: Update) {
  if (step.child_type === 'habit') return <HabitStepFields step={step} update={update} />
  if (step.child_type === 'skill') return <SkillStepFields step={step} update={update} />
  return <TaskStepFields step={step} update={update} />
}

const CADENCE_TO_MODE: Record<string, string> = { daily: 'daily', weekdays: 'weekdays', weekends: 'weekends', weekly: 'days', few_times_a_week: 'days' }

/** What the API stores for a step, from the review's shape. */
export interface ApiStepShape {
  step_id?: string
  step_ref?: string | null
  title: string
  description: string
  estimated_duration_minutes: number
  order: number
  child_type?: string
  habit?: { rrule?: string; scheduled_times_local?: string[]; preferred_days?: string[]; target_cadence?: string; schedule_start_date?: string; duration_minutes?: number } | null
  task?: { target_window?: string; due_date?: string } | null
  habit_plan?: { cadence?: string | null; days?: string[] | null; time_of_day?: string | null; minutes?: number | null } | null
  skill_plan?: Record<string, unknown> | null
  prerequisite_step_refs?: string[] | null
  skill_requirements?: Array<{ skill_step_ref: string; required_level?: string | null; required_capability?: string | null }> | null
  demonstrates_level?: string | null
}

/** The engine's previewed step → the review's shape (a habit's suggested schedule becomes the chooser's state). */
export function fromApiStep(s: ApiStepShape, allowed: string[] = ['task', 'habit']): BreakdownStep {
  const raw = String(s.child_type || 'task')
  const kind = (allowed.includes(raw) ? raw : 'task') as BreakdownStep['child_type']
  const base: BreakdownStep = { step_id: s.step_id, step_ref: s.step_ref || undefined, title: s.title, description: s.description || '', estimated_duration_minutes: s.estimated_duration_minutes, order: s.order, child_type: kind, skill_plan: s.skill_plan || undefined, prerequisite_step_refs: s.prerequisite_step_refs || undefined, skill_requirements: s.skill_requirements || undefined, demonstrates_level: s.demonstrates_level || undefined }
  if (kind !== 'habit') return base
  const plan = s.habit_plan || {}
  const mode = CADENCE_TO_MODE[String(plan.cadence || '')] || ''
  const days = (plan.days || []).map((d) => String(d).slice(0, 2).toUpperCase()).filter((d) => WEEKDAY_CHIPS.some((w) => w.value === d))
  const time = /^\d{2}:\d{2}$/.test(String(plan.time_of_day || '')) ? String(plan.time_of_day) : ''
  return { ...base, estimated_duration_minutes: plan.minutes || base.estimated_duration_minutes, habit: { schedule_mode: mode, schedule_days: days, schedule_time: time } }
}

/** The review's steps → what the API stores: a task with its by-when, a habit with an RRULE, a skill as is. */
export function toApiSteps(steps: BreakdownStep[]): ApiStepShape[] {
  return steps.map((s, i) => {
    const kind = s.child_type || 'task'
    const base: ApiStepShape = { step_id: s.step_id, step_ref: s.step_ref || s.step_id || `step-${i + 1}`, title: s.title, description: s.description || '', estimated_duration_minutes: s.estimated_duration_minutes, order: i + 1, child_type: kind, skill_plan: s.skill_plan || undefined, prerequisite_step_refs: s.prerequisite_step_refs || [], skill_requirements: s.skill_requirements || [], demonstrates_level: s.demonstrates_level || null }
    if (kind === 'task') {
      const when = s.task?.when || ''
      if (when === 'date' && s.task?.due_date) return { ...base, task: { due_date: s.task.due_date } }
      if (when && when !== 'date') return { ...base, task: { target_window: when } }
      return base
    }
    if (kind !== 'habit') return base
    const spec = quickSpec({ schedule_mode: s.habit?.schedule_mode || '', schedule_days: s.habit?.schedule_days || [] })
    const time = (s.habit?.schedule_time || '').trim()
    const habit: NonNullable<ApiStepShape['habit']> = { duration_minutes: s.estimated_duration_minutes || undefined }
    if (spec) {
      habit.rrule = buildRRule(spec); habit.scheduled_times_local = time ? [time] : []; habit.schedule_start_date = todayLocalISO()
      habit.target_cadence = deriveLegacyCadence(spec); habit.preferred_days = deriveLegacyPreferredDays(spec)
    } else if (time) {
      habit.rrule = 'FREQ=DAILY'; habit.scheduled_times_local = [time]; habit.schedule_start_date = todayLocalISO(); habit.target_cadence = 'daily'
    }
    return { ...base, habit }
  })
}
